import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";

import { Subscription } from "rxjs";

import { ConfirmDialogComponent } from "@components/shared";
import { InputSelectOption } from "@components/shared/inputs/input-select/input-select.model";
import { CopyModeEnum } from "@components/shared/value-and-copy-button/value-and-copy-button.model";
import { IconConstants, TextConstants } from "@shared/constants";
import {
  ConfirmDialogResponseEnum,
  RulesetConditionOperatorEnum,
  TargetPathEnum,
} from "@shared/enums";
import { IExpectationItemForm } from "@shared/interfaces/ruleset.interface";
import { AuthenticationService, DocumentTypesService } from "@shared/services";

@Component({
  selector: "app-expectation-item",
  templateUrl: "./expectation-item.component.html",
  styleUrls: ["./expectation-item.component.scss"],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpectationItemComponent implements OnInit, OnDestroy {
  index = input.required<number>();

  isReadOnly = input<boolean>(false);

  shouldDisplayDeleteButton = input<boolean>(false);

  expectationForm = input.required<FormGroup<IExpectationItemForm>>();

  valueOptions = input<InputSelectOption[]>();

  remove = output<void>();

  readonly copyMode = CopyModeEnum.NONE;

  readonly documentTypesService = inject(DocumentTypesService);

  private readonly dialog = inject(MatDialog);

  private readonly authenticationService = inject(AuthenticationService);

  private readonly activeOrganisationId = this.authenticationService.getActiveOrganisationId();

  readonly isEqOperator = signal(true);

  readonly translations = {
    pathPrefix: $localize`:@@ruleset.selectattribute:Select attribute`,
    pathSuffix: $localize`:@@ruleset.selectproperty:Select property`,
    operator: $localize`:@@ruleset.selectoperator:Select operator`,
    value: $localize`:@@ruleset.selectvalue:Select value(s)`,
    selectValue: $localize`:@@ruleset.selectvaluesingular:Select value`,
    deleteTp: TextConstants.DELETE,
  };

  readonly pathPrefixOptions: InputSelectOption[] = [
    {
      label: $localize`:@@ruleset.attacheddocuments:Attached document(s)`,
      value: TargetPathEnum.DOCUMENT,
    },
  ];

  readonly pathSuffixOptions: InputSelectOption[] = [
    {
      label: this.toLowerCaseWhenReadOnly($localize`:@@ruleset.types:Type(s)`),
      value: TargetPathEnum.TYPE,
    },
  ];

  readonly operatorOptions = computed(() => [
    {
      label: this.toLowerCaseWhenReadOnly($localize`:@@ruleset.is:Is`),
      value: RulesetConditionOperatorEnum.EQ,
    },
    {
      label: this.toLowerCaseWhenReadOnly($localize`:@@ruleset.isanyof:Is any of`),
      value: RulesetConditionOperatorEnum.ANY,
    },
  ]);

  private subscriptions = new Subscription();

  ngOnInit() {
    this.subscriptions.add(
      this.expectationForm()
        .get("operator")
        .valueChanges.subscribe((value) => {
          const newValue = value === RulesetConditionOperatorEnum.EQ ? null : [];

          this.expectationForm().get("value").setValue(newValue);
          this.setIsEqOperator(value);
        }),
    );

    this.setIsEqOperator(this.expectationForm().get("operator").value);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private setIsEqOperator(value: RulesetConditionOperatorEnum) {
    this.isEqOperator.set(value === RulesetConditionOperatorEnum.EQ);
  }

  private toLowerCaseWhenReadOnly(value: string) {
    return this.isReadOnly() ? value.toLowerCase() : value;
  }

  onRemove() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: $localize`:@@ruleset.deleteexpectation:Delete expectation`,
        contentText: $localize`:@@ruleset.deleteexpectation.confirm:Are you sure you want to delete the expectation?`,
        confirmButtonColor: "danger",
        confirmButtonText: TextConstants.DELETE,
        confirmButtonIcon: IconConstants.DELETE,
      },
    });

    dialogRef.afterClosed().subscribe((result: ConfirmDialogResponseEnum) => {
      if (result === ConfirmDialogResponseEnum.CONFIRM) {
        this.remove.emit();
      }
    });
  }

  onNewDocumentTypeCreated(option: InputSelectOption) {
    option.value = `/organisations/${this.activeOrganisationId}/document-types/${option.value}`;
  }
}
